import { useRef, useState } from 'react';
import { uploadImage } from '@/lib/api';

interface MultiImageUploadProps {
  value: string[];
  onChange: (urls: string[]) => void;
  folder?: string;
  help?: string;
}

export default function MultiImageUpload({ value, onChange, folder = 'gallery', help }: MultiImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');

  const urls = Array.isArray(value) ? value : [];

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const uploaded = await Promise.all(Array.from(files).map((f) => uploadImage(f, folder)));
      onChange([...urls, ...uploaded]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (idx: number) => onChange(urls.filter((_, i) => i !== idx));

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...urls];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const addUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    onChange([...urls, trimmed]);
    setUrlInput('');
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn-outline whitespace-nowrap text-xs px-3 py-2 cursor-pointer"
        >
          {uploading ? (
            <>
              <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
              Uploading...
            </>
          ) : (
            <>
              <i className="ri-image-add-line" aria-hidden="true" />
              Upload Photos
            </>
          )}
        </button>
        <span className="text-xs text-foreground-400">
          {urls.length > 0 ? `${urls.length} photo${urls.length > 1 ? 's' : ''} added` : 'You can select several photos at once'}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {urls.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {urls.map((url, idx) => (
            <div key={`${url}-${idx}`} className="relative group rounded-md overflow-hidden border border-background-200">
              <img src={url} alt="" className="w-full h-20 object-cover object-top" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  title="Move left"
                  className="w-6 h-6 flex items-center justify-center rounded bg-white/90 text-foreground-700 disabled:opacity-40 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  title="Remove"
                  className="w-6 h-6 flex items-center justify-center rounded bg-red-500 text-white cursor-pointer"
                >
                  <i className="ri-delete-bin-line" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, 1)}
                  disabled={idx === urls.length - 1}
                  title="Move right"
                  className="w-6 h-6 flex items-center justify-center rounded bg-white/90 text-foreground-700 disabled:opacity-40 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="url"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="Or paste an image URL"
          className="flex-1 px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900"
        />
        <button
          type="button"
          onClick={addUrl}
          className="px-4 py-2.5 rounded-md bg-primary-500 text-white text-xs font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          Add
        </button>
      </div>

      {help && <p className="text-xs text-foreground-400">{help}</p>}
      {error && <p className="text-red-600 text-xs">{error}</p>}
    </div>
  );
}